import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, vModelText as _vModelText, withDirectives as _withDirectives, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "formulario-modelo" }
const _hoisted_2 = {
  key: 0,
  class: "info-version"
}
const _hoisted_3 = { class: "badge" }
const _hoisted_4 = {
  key: 1,
  class: "alerta-version"
}
const _hoisted_5 = {
  key: 2,
  class: "info"
}
const _hoisted_6 = {
  key: 3,
  class: "mensaje-vacio"
}
const _hoisted_7 = {
  key: 4,
  class: "indicadores-list"
}
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "indicador-info" }
const _hoisted_10 = { class: "toggle-icon" }
const _hoisted_11 = {
  key: 0,
  class: "indicador-body"
}
const _hoisted_12 = {
  key: 0,
  class: "evaluadores-list"
}
const _hoisted_13 = { class: "evaluador-acciones" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = ["onClick"]
const _hoisted_16 = {
  key: 1,
  class: "mensaje-vacio-sm"
}
const _hoisted_17 = { class: "asignar-form" }
const _hoisted_18 = ["onUpdate:modelValue"]
const _hoisted_19 = ["disabled", "onClick"]
const _hoisted_20 = {
  key: 2,
  class: "error-msg"
}
const _hoisted_21 = { class: "botones" }

import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useGestionIndicadorEvaluadores } from '@/store/GestionIndicadorEvaluadores'
import { useGestionIndicadores } from '@/store/GestionIndicadores'
import { useGestionVersiones } from '@/store/GestionVersiones'
import type Indicador from '@/interfaces/Indicador'
import MensajeConfirmacion from '@/components/MensajeConfirmacion.vue'
import MensajeToast from '@/components/MensajeToast.vue'

const idUsuarioActual = 1


export default /*@__PURE__*/_defineComponent({
  __name: 'AsignarEvaluadoresView',
  setup(__props) {

/**
 * Reparto del trabajo de evaluación sobre los indicadores.
 *
 * Tres tareas: evidencias, instrumentos y calificar. Una asignación sin
 * indicador designa al responsable del proceso.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/version/:id_version/evaluadores` (AsignarEvaluadores) — permiso `procesos.asignar`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

const route = useRoute()
const router = useRouter()
const idVersion = Number(route.params.id_version)
const evalStore = useGestionIndicadorEvaluadores()
const indicadoresStore = useGestionIndicadores()
const versionesStore = useGestionVersiones()
const toastRef = ref<InstanceType<typeof MensajeToast> | null>(null)

const indicadores = ref<Indicador[]>([])
const loadingIndicadores = ref(false)
const versionAprobada = ref(true)
const expandido = ref<Set<number>>(new Set())
const nuevoUsuario = reactive<Record<number, number | null>>({})
const erroresPorIndicador = reactive<Record<number, string>>({})
const showConfirmacion = ref(false)
const idEliminar = ref<number | null>(null)

// Simular id del usuario actual (en producción vendría del store de auth)
function showToast(msg: string, tipo: 'exito' | 'error' = 'exito') {
  toastRef.value?.showToast(msg, tipo)
}

const asignacionesPorIndicador = computed(() => {
  const map: Record<number, typeof evalStore.asignaciones> = {}
  for (const a of evalStore.asignaciones) {
    if (a.id_indicador == null) continue
    if (!map[a.id_indicador]) map[a.id_indicador] = []
    map[a.id_indicador].push(a)
  }
  return map
})

function irAEvaluar(idIndicador: number, idUsuario: number) {
  router.push({
    name: 'EvaluacionIndividual',
    params: { id_version: idVersion, id_indicador: idIndicador },
    query: { id_usuario: String(idUsuario) }
  })
}

function toggleIndicador(id: number) {
  if (expandido.value.has(id)) {
    expandido.value.delete(id)
  } else {
    expandido.value.add(id)
  }
}

async function asignarEvaluador(idIndicador: number) {
  const idUsuario = nuevoUsuario[idIndicador]
  if (!idUsuario || idUsuario <= 0) return
  delete erroresPorIndicador[idIndicador]

  const result = await evalStore.asignar({
    id_version: idVersion,
    id_indicador: idIndicador,
    id_usuario: idUsuario,
    id_asignado_por: idUsuarioActual
  })

  if (result.ok) {
    showToast('Evaluador asignado')
    nuevoUsuario[idIndicador] = null
  } else {
    const msg = result.errors
      ? Object.values(result.errors).flat().join('. ')
      : 'Error al asignar'
    erroresPorIndicador[idIndicador] = msg
    showToast(msg, 'error')
  }
}

function confirmarEliminar(id: number) {
  idEliminar.value = id
  showConfirmacion.value = true
}

async function ejecutarEliminar() {
  showConfirmacion.value = false
  if (!idEliminar.value) return
  const result = await evalStore.eliminar(idEliminar.value)
  if (result.ok) {
    showToast('Asignación eliminada')
  } else {
    showToast('Error al eliminar asignación', 'error')
  }
  idEliminar.value = null
}

onMounted(async () => {
  if (!Number.isFinite(idVersion) || idVersion <= 0) return
  loadingIndicadores.value = true
  try {
    const version = await versionesStore.getVersionById(idVersion)
    if (!version || version.estado !== 'completado') {
      versionAprobada.value = false
      loadingIndicadores.value = false
      return
    }
    await Promise.all([
      indicadoresStore.cargarTodos(),
      evalStore.cargar({ id_version: idVersion })
    ])
    indicadores.value = indicadoresStore.indicadores
  } finally {
    loadingIndicadores.value = false
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[3] || (_cache[3] = _createElementVNode("h1", null, "Asignar Evaluadores por Indicador", -1)),
    _cache[4] || (_cache[4] = _createElementVNode("div", { class: "divider" }, null, -1)),
    (_unref(idVersion))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("span", _hoisted_3, "Versión #" + _toDisplayString(_unref(idVersion)), 1)
        ]))
      : _createCommentVNode("", true),
    (!versionAprobada.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, _cache[2] || (_cache[2] = [
          _createTextVNode(" La versión no está en estado "),
          _createElementVNode("strong", null, "completado", -1),
          _createTextVNode(". No se pueden asignar evaluadores hasta que la versión sea completada. ")
        ])))
      : _createCommentVNode("", true),
    (loadingIndicadores.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, "Cargando indicadores..."))
      : (!indicadores.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, "No hay indicadores disponibles para esta versión."))
        : (_openBlock(), _createElementBlock("div", _hoisted_7, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(indicadores.value, (ind) => {
              return (_openBlock(), _createElementBlock("div", {
                key: ind.id_indicador,
                class: "indicador-card"
              }, [
                _createElementVNode("div", {
                  class: "indicador-header",
                  onClick: ($event: any) => (toggleIndicador(ind.id_indicador))
                }, [
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("strong", null, _toDisplayString(ind.nombre), 1),
                    _createElementVNode("small", null, "ID: " + _toDisplayString(ind.id_indicador), 1)
                  ]),
                  _createElementVNode("span", _hoisted_10, _toDisplayString(expandido.value.has(ind.id_indicador) ? '▾' : '▸'), 1)
                ], 8, _hoisted_8),
                (expandido.value.has(ind.id_indicador))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                      (asignacionesPorIndicador.value[ind.id_indicador]?.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(asignacionesPorIndicador.value[ind.id_indicador], (asig) => {
                              return (_openBlock(), _createElementBlock("div", {
                                key: asig.id,
                                class: "evaluador-item"
                              }, [
                                _createElementVNode("span", null, [
                                  _createTextVNode("Usuario #" + _toDisplayString(asig.id_usuario) + " ", 1),
                                  _createElementVNode("small", null, "(asignado por #" + _toDisplayString(asig.id_asignado_por) + ")", 1)
                                ]),
                                _createElementVNode("div", _hoisted_13, [
                                  _createElementVNode("button", {
                                    class: "btn btn-sm guardar",
                                    onClick: _withModifiers(($event: any) => (irAEvaluar(ind.id_indicador, asig.id_usuario)), ["stop"])
                                  }, "Evaluar", 8, _hoisted_14),
                                  _createElementVNode("button", {
                                    class: "btn btn-sm salir",
                                    onClick: ($event: any) => (confirmarEliminar(asig.id!))
                                  }, "✕", 8, _hoisted_15)
                                ])
                              ]))
                            }), 128))
                          ]))
                        : (_openBlock(), _createElementBlock("p", _hoisted_16, "Sin evaluadores asignados.")),
                      _createElementVNode("div", _hoisted_17, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": ($event: any) => ((nuevoUsuario[ind.id_indicador]) = $event),
                          type: "number",
                          min: "1",
                          placeholder: "ID del usuario evaluador",
                          class: "form-control form-sm"
                        }, null, 8, _hoisted_18), [
                          [
                            _vModelText,
                            nuevoUsuario[ind.id_indicador],
                            void 0,
                            { number: true }
                          ]
                        ]),
                        _createElementVNode("button", {
                          class: "btn btn-sm guardar",
                          disabled: !nuevoUsuario[ind.id_indicador],
                          onClick: ($event: any) => (asignarEvaluador(ind.id_indicador))
                        }, " Asignar ", 8, _hoisted_19)
                      ]),
                      (erroresPorIndicador[ind.id_indicador])
                        ? (_openBlock(), _createElementBlock("div", _hoisted_20, _toDisplayString(erroresPorIndicador[ind.id_indicador]), 1))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ]))
            }), 128))
          ])),
    _createElementVNode("div", _hoisted_21, [
      _createElementVNode("button", {
        class: "btn salir",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$router.back()))
      }, "Volver")
    ]),
    _createVNode(MensajeConfirmacion, {
      visible: showConfirmacion.value,
      title: "Eliminar Asignación",
      message: "¿Desea eliminar esta asignación de evaluador?",
      confirmText: "Eliminar",
      cancelText: "Cancelar",
      onConfirm: ejecutarEliminar,
      onCancel: _cache[1] || (_cache[1] = ($event: any) => (showConfirmacion.value = false))
    }, null, 8, ["visible"]),
    _createVNode(MensajeToast, {
      ref_key: "toastRef",
      ref: toastRef
    }, null, 512)
  ]))
}
}

})